import React, { useState } from 'react';
import axios from 'axios';
import { Badge, Button } from '@opendate-app/setlist';
import GivingConfirmModal from '../../shared/components/GivingConfirmModal';
import formatPledgeAmount from '../../shared/utils/formatPledgeAmount';
import { toastError, toastSuccess } from '../../../shared/toastHelper';

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const daysSince = (iso) => Math.max(0, Math.round((startOfDay(new Date()) - startOfDay(new Date(iso))) / DAY_MS));

const OPEN_STATUSES = ['scheduled', 'overdue', 'failed'];

export default function ScheduleControls({ teamId, pledge, onUpdated }) {
  const [dialog, setDialog] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null);
  const [reduceTotal, setReduceTotal] = useState(false);
  const [pending, setPending] = useState(false);
  const base = `/teams/${teamId}/crm/pledges/${pledge.id}`;

  if (pledge.schedule_canceled_at) {
    return <Badge color="gray">Schedule canceled</Badge>;
  }

  const close = () => {
    setDialog(null);
    setResumeChoice(null);
    setReduceTotal(false);
  };

  const run = async (path, payload, successMessage) => {
    setPending(true);
    try {
      const { data } = payload ? await axios.post(`${base}/${path}.json`, payload) : await axios.post(`${base}/${path}.json`);
      onUpdated(data);
      toastSuccess(successMessage);
      close();
    } catch (e) {
      toastError(e?.response?.data?.error || 'Could not update the schedule. Try again.');
    } finally {
      setPending(false);
    }
  };

  const paused = !!pledge.autopay_paused_at;
  const pausedDays = paused ? daysSince(pledge.autopay_paused_at) : 0;
  const rows = pledge.installments || [];
  const hasOpenInstallments = rows.some((row) => OPEN_STATUSES.includes(row.derived_status));
  const missed = rows.filter((row) => row.derived_status === 'overdue');
  const missedCents = missed.reduce((sum, row) => sum + Number(row.amount_cents || 0), 0);
  const inFlightCents = rows.filter((row) => row.derived_status === 'processing')
    .reduce((sum, row) => sum + Number(row.charged_amount_cents || 0), 0);
  const collectedCents = Number(pledge.received_amount_cents || 0) + inFlightCents;
  if (!paused && !hasOpenInstallments) return null;

  return (
    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
      {paused && <Badge color="warning" dot>Autopay paused</Badge>}
      {paused ? (
        <Button variant="secondary" size="sm" onClick={() => setDialog('resume')}>Resume autopay</Button>
      ) : (
        pledge.card && <Button variant="secondary" size="sm" onClick={() => setDialog('pause')}>Pause autopay</Button>
      )}
      {hasOpenInstallments && (
        <Button variant="link-destructive" size="sm" onClick={() => setDialog('cancel')}>Cancel remaining schedule</Button>
      )}

      <GivingConfirmModal
        open={dialog === 'pause'}
        title="Pause autopay?"
        body="Opendate stops charging this pledge's card until you resume. Installments that fall due while paused aren't charged, and the pledge won't be marked delinquent."
        confirmLabel="Pause"
        pendingLabel="Pausing…"
        pending={pending}
        onConfirm={() => run('pause_autopay', null, 'Autopay paused.')}
        onCancel={close}
      />

      <GivingConfirmModal
        open={dialog === 'resume'}
        title="Resume autopay?"
        body="Choose what happens to the payments that fell due while autopay was paused."
        confirmLabel="Resume"
        pendingLabel="Resuming…"
        pending={pending}
        confirmDisabled={!resumeChoice}
        onConfirm={() => run('resume_autopay', { push_out: resumeChoice === 'push_out' }, 'Autopay resumed.')}
        onCancel={close}
      >
        <label className="d-flex align-items-start mb-2" style={{ gap: 8 }}>
          <input type="radio" name="resume_choice" value="charge_now" checked={resumeChoice === 'charge_now'} onChange={() => setResumeChoice('charge_now')} />
          <span>
            {missed.length > 0
              ? `Charge the ${missed.length} missed ${missed.length === 1 ? 'payment' : 'payments'} (${formatPledgeAmount(missedCents)}) now`
              : 'Charge missed payments now (none are due yet)'}
          </span>
        </label>
        <label className="d-flex align-items-start" style={{ gap: 8 }}>
          <input type="radio" name="resume_choice" value="push_out" checked={resumeChoice === 'push_out'} onChange={() => setResumeChoice('push_out')} />
          <span>Push the remaining payments out by {pausedDays} {pausedDays === 1 ? 'day' : 'days'}, the length of the pause</span>
        </label>
      </GivingConfirmModal>

      <GivingConfirmModal
        open={dialog === 'cancel'}
        title="Cancel the remaining schedule?"
        body="Every unpaid installment is canceled and autopay stops. Payments already received stay on the pledge. This can't be undone."
        confirmLabel="Cancel schedule"
        cancelLabel="Keep schedule"
        pendingLabel="Canceling…"
        pending={pending}
        danger
        onConfirm={() => run('cancel_schedule', { reduce_total: reduceTotal }, 'Remaining schedule canceled.')}
        onCancel={close}
      >
        {collectedCents > 0 && (
          <label className="d-flex align-items-start" style={{ gap: 8 }}>
            <input type="checkbox" checked={reduceTotal} onChange={(e) => setReduceTotal(e.target.checked)} />
            <span>Lower the pledge total to {formatPledgeAmount(collectedCents)} collected</span>
          </label>
        )}
      </GivingConfirmModal>
    </div>
  );
}
